import { useCallback, useEffect, useState } from "react";
import { countSubscriptions, removeSubscription, saveSubscription } from "../data/push";
import {
  accepted,
  askDue,
  declined,
  lastSavedEndpoint,
  markPushAskedThisSession,
  type PushMemory,
  type PushPermission,
  pushAskedThisSession,
  readPushMemory,
  rememberSavedEndpoint,
  writePushMemory,
} from "./ask";
import {
  currentSubscription,
  pushPermission,
  requestAndSubscribe,
  subscribeNow,
  toRecord,
  unsubscribe,
} from "./subscribe";

/**
 * Why the sheet is open, and therefore what it says.
 *
 * Carried rather than inferred, because the whole argument for asking in
 * context is that the copy can name the actual thing: "we'll tell you on the
 * 14th, two days before the paneer goes off" lands where "enable notifications
 * to stay updated" does not. A reason with no item in it would be the generic
 * pitch wearing a contextual trigger.
 */
export interface AskContext {
  /** What the user just added or opened. */
  name: string;
  /** Days until it goes off. Null when the item has no date. */
  daysLeft: number | null;
}

/** What Settings shows, and what the larder needs to know to stay quiet. */
export type PushStatus =
  /** No push on this browser — an iOS tab, or no VAPID key configured. */
  | "unsupported"
  /** Blocked at the browser. Only site settings can undo it. */
  | "blocked"
  /** Available, not turned on. */
  | "off"
  /** Permission granted and a live subscription is saved. */
  | "on";

export interface PushApi {
  status: PushStatus;
  /** How many devices this account can be reached on. */
  devices: number;
  /** Non-null when the soft ask should be on screen. */
  pending: AskContext | null;
  /**
   * A contextual trigger fired. Shows the sheet only if an ask is actually due
   * — the caller does not have to know the rules, and calls this freely.
   */
  consider: (ctx: AskContext) => void;
  /** They said yes to our sheet. Fires the native dialog. Must be in a gesture. */
  allow: () => Promise<boolean>;
  /** They said "not now". The native dialog is never fired. */
  dismiss: () => void;
  /** Settings: turn it on deliberately, ignoring the backoff. */
  enable: () => Promise<boolean>;
  /** Settings: stop reminders on this device. */
  disable: () => Promise<void>;
}

/**
 * Permission, subscription and the decision to ask, in one hook.
 *
 * The reliability work is the `useEffect` below rather than anything the user
 * sees: subscriptions expire, and a subscription that expired silently is
 * indistinguishable from a feature that was never built.
 *
 * @param uid the signed-in user. Subscriptions are stored per account, so a
 *   second person on a shared phone gets their own, and signing out stops
 *   writing to the first person's.
 */
export function usePush(uid: string | null): PushApi {
  const [permission, setPermission] = useState<PushPermission>(() => pushPermission());
  const [subscribed, setSubscribed] = useState(false);
  const [devices, setDevices] = useState(0);
  const [memory, setMemory] = useState<PushMemory>(() => readPushMemory());
  const [asked, setAsked] = useState(() => (uid ? pushAskedThisSession(uid) : false));
  const [pending, setPending] = useState<AskContext | null>(null);

  // Signing in as someone else in the same tab is a new ask.
  useEffect(() => {
    setAsked(uid ? pushAskedThisSession(uid) : false);
  }, [uid]);

  /**
   * Heal on every launch. This is the single most important thing here.
   *
   * A Web Push subscription is not permanent. Browsers rotate them, clearing
   * site data destroys them, and a long-idle Safari web app has its dropped
   * outright. None of that produces an error anyone sees — reminders just stop,
   * and the user concludes the app is broken rather than that it needs a tap.
   *
   * So: if permission is granted and there is no live subscription, make one,
   * silently and without any dialog. And re-save whatever subscription exists,
   * because a record can also be lost at the Firestore end — a device that is
   * subscribed at the browser but absent from the database is exactly as quiet
   * as one that is not subscribed at all.
   */
  useEffect(() => {
    if (!uid) {
      setSubscribed(false);
      setDevices(0);
      return;
    }
    let live = true;

    void (async () => {
      const p = pushPermission();
      if (live) setPermission(p);
      if (p !== "granted") {
        if (live) setSubscribed(false);
        return;
      }

      const existing = await currentSubscription();
      const record = existing ? toRecord(existing) : await subscribeNow();
      if (!record?.keys.p256dh) {
        if (live) setSubscribed(false);
        return;
      }

      // The browser has a subscription, so this device can be reached — whether
      // or not we end up writing anything below.
      if (live) setSubscribed(true);

      /**
       * Write only when something actually changed.
       *
       * The heal has to RUN every launch, because a rotated or dropped
       * subscription is invisible until you look. It does not have to WRITE
       * every launch, and a healthy device is the overwhelmingly common case —
       * so the endpoint we last stored is remembered locally and an unchanged
       * one costs nothing at all.
       */
      if (lastSavedEndpoint(uid) === record.endpoint) return;

      try {
        await saveSubscription(uid, record);
        rememberSavedEndpoint(uid, record.endpoint);
      } catch {
        // Offline, or rules refused. Nothing is remembered, so the next launch
        // tries again — and there is nothing the user could do about it now.
        return;
      }

      try {
        const n = await countSubscriptions(uid);
        if (live) setDevices(n);
      } catch {
        // Counting is for Settings copy only. Not worth surfacing.
      }
    })();

    return () => {
      live = false;
    };
  }, [uid]);

  const remember = useCallback((next: PushMemory) => {
    setMemory(next);
    writePushMemory(next);
  }, []);

  const consider = useCallback(
    (ctx: AskContext) => {
      if (!uid) return;
      if (
        !askDue({
          permission,
          subscribed,
          memory,
          askedThisSession: asked,
          nowMs: Date.now(),
        })
      )
        return;
      markPushAskedThisSession(uid);
      setAsked(true);
      setPending(ctx);
    },
    [uid, permission, subscribed, memory, asked],
  );

  /** Shared by the sheet's yes and by the Settings switch. */
  const turnOn = useCallback(async (): Promise<boolean> => {
    if (!uid) return false;
    const record = await requestAndSubscribe();
    // Re-read rather than infer: the user may have blocked, and `denied` is
    // what stops the sheet ever appearing again.
    setPermission(pushPermission());
    if (!record) return false;
    try {
      await saveSubscription(uid, record);
      rememberSavedEndpoint(uid, record.endpoint);
    } catch {
      return false;
    }
    setSubscribed(true);
    setDevices((n) => Math.max(n, 1));
    return true;
  }, [uid]);

  const allow = useCallback(async () => {
    remember(accepted(memory, Date.now()));
    const ok = await turnOn();
    setPending(null);
    return ok;
  }, [memory, remember, turnOn]);

  const dismiss = useCallback(() => {
    remember(declined(memory, Date.now()));
    setPending(null);
  }, [memory, remember]);

  const disable = useCallback(async () => {
    const endpoint = await unsubscribe();
    setSubscribed(false);
    setDevices((n) => Math.max(0, n - 1));
    // Forget what we stored, so turning it back on writes rather than assuming
    // the record that was just deleted is still there.
    if (uid) rememberSavedEndpoint(uid, null);
    if (uid && endpoint) {
      try {
        await removeSubscription(uid, endpoint);
      } catch {
        // The browser has already stopped delivering. A stale record costs one
        // failed send, which the server prunes on the 410.
      }
    }
  }, [uid]);

  const status: PushStatus =
    permission === "unsupported"
      ? "unsupported"
      : permission === "denied"
        ? "blocked"
        : permission === "granted" && subscribed
          ? "on"
          : "off";

  return { status, devices, pending, consider, allow, dismiss, enable: turnOn, disable };
}
